import * as React from 'react'
import { DateTime } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMoon } from '@fortawesome/free-solid-svg-icons/faMoon'
import { faPowerOff } from '@fortawesome/free-solid-svg-icons/faPowerOff'
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt'
import { faBatteryFull } from '@fortawesome/free-solid-svg-icons/faBatteryFull'
import { faPlug } from '@fortawesome/free-solid-svg-icons/faPlug'
import useDayHistoryStore from '../../../../store/dayHistoryStore.ts'
import authStore from '../../../../store/authStore.ts'
import { getProgress } from '../../../../helpers/dayHistoryHelper.ts'
import { IDayHistoryGap, IDayHistoryEvent } from '../../../../interfaces/dayHistory.ts'

const EVENT_ICONS: Record<string, any> = {
    outOfCharger: faPlug,
    inCharger: faPlug,
    powerUp: faBolt,
    powerDown: faPowerOff,
    fullCharge: faBatteryFull,
    wakeResponse: faMoon,
    sleepResponse: faMoon,
    beaconInRange: faBolt,
    beaconOutOfRange: faBolt,
}

const EVENT_LABELS: Record<string, string> = {
    outOfCharger: 'Out of charger',
    inCharger: 'In charger',
    powerUp: 'Power on',
    powerDown: 'Power off',
    fullCharge: 'Full charge',
    wakeResponse: 'Wake',
    sleepResponse: 'Sleep',
    beaconInRange: 'Power save on',
    beaconOutOfRange: 'Power save off',
}

/**
 * Timeline scrubber bar for Day History.
 * Shows:
 * - A full-width bar representing the day's duration
 * - Gray/dimmed regions for gaps
 * - Small event icons at their time positions
 * - A draggable playhead showing current progress
 */
export const DayHistoryTimeline: React.FC = () => {
    const pathData = useDayHistoryStore(s => s.pathData)
    const currentTime = useDayHistoryStore(s => s.currentTime)
    const isPlaying = useDayHistoryStore(s => s.isPlaying)
    const timezone = authStore(s => s.effectiveTimezone)
    const zone = timezone().value

    const barRef = React.useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = React.useState(false)

    if (!pathData) return null

    const { duration, gaps, events } = pathData
    const totalDuration = duration.end - duration.start
    if (totalDuration <= 0) return null

    const progress = getProgress(currentTime, duration.start, duration.end)

    // Convert a time to a percentage position on the bar
    const timeToPercent = (time: number) => {
        return ((time - duration.start) / totalDuration) * 100
    }

    // Handle click/drag on the timeline bar
    const scrubToPosition = (clientX: number) => {
        if (!barRef.current) return
        const rect = barRef.current.getBoundingClientRect()
        const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
        const percent = x / rect.width
        const newTime = duration.start + percent * totalDuration

        useDayHistoryStore.getState().setCurrentTime(newTime)
    }

    const handlePointerDown = (e: React.PointerEvent) => {
        e.preventDefault()
        setIsDragging(true)
        // Pause during scrub
        if (isPlaying) {
            useDayHistoryStore.getState().setIsPlaying(false)
        }
        scrubToPosition(e.clientX)
        ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    }

    const handlePointerMove = (e: React.PointerEvent) => {
        if (!isDragging) return
        scrubToPosition(e.clientX)
    }

    const handlePointerUp = (e: React.PointerEvent) => {
        if (!isDragging) return
        setIsDragging(false)
        ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
    }

    // Gap regions (rendered as dimmed overlay sections)
    const gapRegions = gaps.map((gap: IDayHistoryGap, i: number) => {
        const left = timeToPercent(gap.startTime)
        const width = timeToPercent(gap.endTime) - left
        return (
            <div
                key={`gap-${i}`}
                className={'absolute top-0 bottom-0 bg-gray-400/40 rounded-sm'}
                style={{ left: `${left}%`, width: `${width}%` }}
                title={`${gap.reason} (${formatDuration(gap.endTime - gap.startTime)})`}
            />
        )
    })

    // Event markers (small icons positioned along the bar)
    const eventMarkers = events.map((event: IDayHistoryEvent, i: number) => {
        const left = timeToPercent(event.time)
        const icon = EVENT_ICONS[event.type]
        if (!icon) return null
        const timeLabel = DateTime.fromSeconds(event.time).setZone(zone).toFormat('h:mm a')
        const eventLabel = EVENT_LABELS[event.type] || event.type
        return (
            <div
                key={`event-${i}`}
                className={'tooltip tooltip-top absolute -top-3 -translate-x-1/2 text-secondary opacity-70'}
                style={{ left: `${left}%` }}
                data-tip={`${eventLabel} · ${timeLabel}`}
            >
                <FontAwesomeIcon icon={icon} size={'xs'} />
            </div>
        )
    })

    return (
        <div className={'w-full flex flex-col gap-1'}>
            {/* Timeline bar */}
            <div
                ref={barRef}
                className={'relative w-full h-3 bg-primary/20 rounded-full cursor-pointer touch-none select-none'}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
            >
                {/* Gap regions — only shown in upcoming portion */}
                {gapRegions}

                {/* Progress fill — above gaps so traveled portion shows blue */}
                <div
                    className={'absolute top-0 left-0 bottom-0 bg-primary rounded-full pointer-events-none z-[1]'}
                    style={{ width: `${progress * 100}%` }}
                />

                {/* Playhead */}
                <div
                    className={'absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 bg-primary border-2 border-white rounded-full shadow pointer-events-none z-[2]'}
                    style={{ left: `${progress * 100}%` }}
                />

                {/* Event markers above the bar */}
                {eventMarkers}
            </div>

            {/* Time labels */}
            <div className={'flex justify-between text-xs text-gray-500 px-1'}>
                <span>{DateTime.fromSeconds(duration.start).setZone(zone).toFormat('ha').toLowerCase()}</span>
                <span>{DateTime.fromSeconds(duration.end).setZone(zone).toFormat('ha').toLowerCase()}</span>
            </div>
        </div>
    )
}

/** Format a duration in seconds as a human-readable string */
const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
}
